import { AlarmClock } from 'lucide-react';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AlwaysDownSelect from '../../components/common/AlwaysDownSelect';
import '../../styles/contents/shortFormQuiz/ShortFormQuizStudentPage.css';
import { db, checkFirebaseConnection } from '../../shared/api/firebase';
import { doc, getDoc, setDoc, updateDoc, collection, addDoc, serverTimestamp, onSnapshot, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { getAllStudentNames, addShortFormQuizActivityRecord, upsertShortFormQuizClassActivity } from '../../services/firebaseService';
import { preloadImages } from '../../shared/utils/imagePreloader';
import { shortFormCumulativeRecordGenerator } from '../../services/ShortFormCumulativeRecordGenerator';
import { getAuth } from 'firebase/auth';
// 게임 컴포넌트 Import
import ShortFormQuizBubbleWrap from './ShortFormQuizBubbleWrap';
import ShortFormQuizBalloonPop from './ShortFormQuizBalloonPop';
import ShortFormQuizRobotFactory from './ShortFormQuizRobotFactory';
import ShortFormQuizEraseAndPick from './ShortFormQuizEraseAndPick';
// 공통 리소스 Import
import GameScreenLayout from '../../components/common/GameScreenLayout';
import { getGameImageUrls, getGameTypeByStage, preloadGameImages } from './gameImageUrls';
import globalImageCache from './globalImageCache';

// 이미지 경로 상수들
const coverImage = '/assets/images/contents/shortForm/erase/Cover.jpg';
const bubble = '/assets/images/contents/shortForm/bubble/Bubble.png';
const bubble_filled = '/assets/images/contents/shortForm/bubble/Bubble_Poped.png';

// ===============================================
// 🛠️ 게임 설정 구성 (여기서 게임들을 쉽게 관리하세요!)
// ===============================================
const GAME_CONFIG = {
  // ----------------------------------------
  // 📝 사용 가능한 게임 타입 정의
  // ----------------------------------------
  availableGames: [
    {
      id: 'BubbleWrap',              // 🔑 고유 식별자 (변경 금지)
      name: '버블 터뜨리기',          // 📛 게임 이름 (표시용)
      component: ShortFormQuizBubbleWrap,  // 🎮 실제 게임 컴포넌트
      tutorialMessage: '튜토리얼\n뽁뽁이를 모두 터뜨리세요!', // 💬 튜토리얼 메시지
      enabled: true,                 // ✅ 활성화 여부 (false로 설정하면 비활성화)
    },
    {
      id: 'BalloonPop', 
      name: '풍선 터뜨리기',
      component: ShortFormQuizBalloonPop,
      tutorialMessage: '튜토리얼\n풍선을 모두 터뜨리세요!',
      enabled: true,
    },
    {
      id: 'RobotFactory',
      name: '로봇 조립하기',
      component: ShortFormQuizRobotFactory,
      tutorialMessage: '튜토리얼\n로봇을 조립해보세요!',
      enabled: true,
    },
    {
      id: 'EraseAndPick',
      name: '지우고 고르기',
      component: ShortFormQuizEraseAndPick,
      tutorialMessage: '튜토리얼\n드래그하여 보물을 찾아내세요!',
      enabled: true,
    }
    // ----------------------------------------
    // 🔧 새 게임 추가 템플릿:
    // ----------------------------------------
    // {
    //   id: 'NewGameID',              // 고유한 ID (영문, 숫자, 언더스코어만)
    //   name: '새로운 게임',           // 게임 이름
    //   component: ShortFormQuizNewGame, // import한 컴포넌트
    //   tutorialMessage: '튜토리얼: 새 게임을 해보세요!',
    //   enabled: true,                // true: 활성화, false: 비활성화
    // }
  ],

  // ----------------------------------------
  // 🎯 튜토리얼 단계 설정
  // ----------------------------------------
  tutorial: {
    // 1단계 튜토리얼 게임 고정 설정
    stage1Game: 'BubbleWrap',  // null: 랜덤 선택 | 'GameID': 특정 게임 고정
    
    // 2단계 튜토리얼 게임 고정 설정  
    stage2Game: 'BalloonPop',  // null: 랜덤 선택 | 'GameID': 특정 게임 고정
    
    // 3단계 튜토리얼 게임 고정 설정
    stage3Game: 'RobotFactory',  // null: 랜덤 선택 | 'GameID': 특정 게임 고정
    
    // 4단계 튜토리얼 게임 고정 설정
    stage4Game: 'EraseAndPick',  // null: 랜덤 선택 | 'GameID': 특정 게임 고정
    
    // 💡 예시:
    // stage1Game: 'BubbleWrap',   // 1단계는 항상 버블 게임
    // stage2Game: 'BalloonPop',   // 2단계는 항상 풍선 게임
  },

  // ----------------------------------------
  // 🎲 퀴즈 단계 게임 선택 방식
  // ----------------------------------------
  quizGameSelection: {
    // 📋 선택 모드:
    mode: 'sequence',
    // 'random'   : 매 문제마다 활성화된 게임 중 랜덤 선택
    // 'sequence' : 지정된 순서대로 반복 (sequence 배열 사용)
    // 'fixed'    : 하나의 게임만 계속 사용 (fixedGameId 사용)
    
    // 🎯 고정 모드에서 사용할 게임 ID
    fixedGameId: 'EraseAndPick',
    // mode가 'fixed'일 때만 사용됨
    
    // 🔄 순서 모드에서 사용할 게임 순서
    sequence: ['BubbleWrap', 'BalloonPop', 'RobotFactory', 'EraseAndPick'],
    // mode가 'sequence'일 때 사용됨
    // 비어있으면 availableGames의 활성화된 게임들을 순서대로 사용
    // 예시: ['BubbleWrap', 'BalloonPop', 'RobotFactory', 'EraseAndPick']
    //       → 문제1: BubbleWrap, 문제2: BalloonPop, 문제3: RobotFactory, 문제4: BubbleWrap...
  }
};

// ----------------------------------------
// 💡 설정 예시:
// ----------------------------------------
// 
// 🔸 모든 문제를 버블 게임으로만:
//   → mode: 'fixed', fixedGameId: 'BubbleWrap'
//
// 🔸 게임을 순서대로 반복:
//   → mode: 'sequence', sequence: ['BubbleWrap', 'BalloonPop']
//
// 🔸 로봇 게임만 비활성화:
//   → RobotFactory의 enabled: false
//
// 🔸 튜토리얼 1단계는 항상 버블, 2단계는 랜덤:
//   → stage1Game: 'BubbleWrap', stage2Game: null
//
// ----------------------------------------

// ===============================================

// ===============================================
// 🔧 게임 관리 유틸리티 함수들
// ===============================================

/**
 * 활성화된 게임들만 필터링하여 반환
 */
const getEnabledGames = () => {
  return GAME_CONFIG.availableGames.filter(game => game.enabled);
};

/**
 * 게임 ID로 게임 설정 객체 찾기
 */
const getGameById = (gameId) => {
  return GAME_CONFIG.availableGames.find(game => game.id === gameId);
};

/**
 * 게임 ID에 해당하는 컴포넌트 반환
 */
const getGameComponent = (gameId) => {
  const game = getGameById(gameId);
  return game ? game.component : null;
};

  /**
   * 게임 ID에 해당하는 튜토리얼 메시지 반환
   */
  const getTutorialMessage = (gameId) => {
    const game = getGameById(gameId);
    return game ? game.tutorialMessage : '튜토리얼을 시작합니다!';
  };

  /**
   * 게임 ID에 해당하는 준비 메시지 반환
   */
  const getReadyMessage = (gameId) => {
    switch (gameId) {
      case 'EraseAndPick':
        return '지워라!';
      case 'BubbleWrap':
        return '터뜨려라!';
      case 'BalloonPop':
        return '당겼다 떼라!';
      case 'RobotFactory':
        return '조립해라!';
      default:
        return '준비하세요!';
    }
  };

/**
 * 퀴즈 단계용 게임 시퀀스 생성
 */
const generateQuizGameSequence = (questionCount) => {
  const enabledGames = getEnabledGames();
  if (enabledGames.length === 0) {
    console.warn('활성화된 게임이 없습니다. BubbleWrap을 기본값으로 사용합니다.');
    return Array(questionCount).fill('BubbleWrap');
  }

  const { mode, fixedGameId, sequence } = GAME_CONFIG.quizGameSelection;
  
  switch (mode) {
    case 'fixed':
      const fixedGame = getGameById(fixedGameId);
      const gameId = (fixedGame && fixedGame.enabled) ? fixedGameId : enabledGames[0].id;
      return Array(questionCount).fill(gameId);
      
    case 'sequence':
      const gameSequence = sequence.length > 0 
        ? sequence.filter(id => getGameById(id)?.enabled) 
        : enabledGames.map(g => g.id);
      
      if (gameSequence.length === 0) return Array(questionCount).fill(enabledGames[0].id);
      
      const result = [];
      for (let i = 0; i < questionCount; i++) {
        result.push(gameSequence[i % gameSequence.length]);
      }
      return result;
      
    case 'random':
    default:
      return Array(questionCount).fill(null).map(() => {
        const randomIndex = Math.floor(Math.random() * enabledGames.length);
        return enabledGames[randomIndex].id;
      });
  }
};

/**
 * 튜토리얼 단계용 게임 ID 반환
 */
const getTutorialGame = (stage) => {
  const enabledGames = getEnabledGames();
  if (enabledGames.length === 0) return 'BubbleWrap';
  
  if (stage === 1) {
    const configGame = GAME_CONFIG.tutorial.stage1Game;
    if (configGame && getGameById(configGame)?.enabled) {
      return configGame;
    }
  } else if (stage === 2) {
    const configGame = GAME_CONFIG.tutorial.stage2Game;
    if (configGame && getGameById(configGame)?.enabled) {
      return configGame;
    }
  } else if (stage === 3) {
    const configGame = GAME_CONFIG.tutorial.stage3Game;
    if (configGame && getGameById(configGame)?.enabled) {
      return configGame;
    }
  } else if (stage === 4) {
    const configGame = GAME_CONFIG.tutorial.stage4Game;
    if (configGame && getGameById(configGame)?.enabled) {
      return configGame;
    }
  }
  
  // 설정이 없거나 비활성화된 경우 랜덤 선택
  const randomIndex = Math.floor(Math.random() * enabledGames.length);
  return enabledGames[randomIndex].id;
};

// ===============================================

const ShortFormQuizStudentPage = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const location = useLocation();

  // 애니메이션 CSS 추가
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 0.4; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.2); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [studentId, setStudentId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [step, setStep] = useState(0); // 0: 이름 입력, 1: 퀴즈 진행, 2: 결과
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [phase, setPhase] = useState('join'); // 'join' | 'waiting' | 'final'
  const [gameSequence, setGameSequence] = useState([]);

  // 이름 입력/선택 관련
  const [studentNames, setStudentNames] = useState([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null); // 선택된 학생의 ID 저장
  const [isJoining, setIsJoining] = useState(false);
  const [sessionStatus, setSessionStatus] = useState('idle'); // 'idle' | 'started'
  const [totalStages, setTotalStages] = useState(5);
  const [currentStage, setCurrentStage] = useState(1); // session에서 온 값(참고용)
  const [localStage, setLocalStage] = useState(1); // 실제 진행 표시용(교사 신호 없이 자동)
  const [perStageSeconds, setPerStageSeconds] = useState(30);
  // 스테이지 표시용 로컬 페이즈 (pre → play → feedback → end)
  const [stagePhase, setStagePhase] = useState('idle'); // 'idle' | 'pre' | 'play' | 'feedback' | 'end'
  const [preLeft, setPreLeft] = useState(2);
  const [playLeft, setPlayLeft] = useState(0);
  const [endLeft, setEndLeft] = useState(2);
  
  // 현재 게임이 진행 중인지 추적하는 상태 (전체 시간 종료 시 게임 완료까지 대기용)
  const [isGameInProgress, setIsGameInProgress] = useState(false);
  const canvasRef = React.useRef(null);
  
  // 전체 게임 이미지 미리 로딩 관련 상태
  const [allImagesLoading, setAllImagesLoading] = useState(false);
  const [allImagesProgress, setAllImagesProgress] = useState(0);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);
  const preloadCancelRef = React.useRef(null);
  // 게임 상태 관리용 refs
  const correctCountRef = React.useRef(0);
  const totalQuestionsRef = React.useRef(0);
  const stageAnsweredRef = React.useRef(false);
  const answersRef = React.useRef([]); // 각 문항 선택 인덱스 기록
  const wrongIndexesRef = React.useRef([]); // 1-based 틀린 문항 번호
  const [feedbackActive, setFeedbackActive] = useState(false);
  const [feedbackIsSuccess, setFeedbackIsSuccess] = useState(false);
  const [feedbackRect, setFeedbackRect] = useState(null); // {x,y,w,h}

  // 단순 플레이 모드(교사가 start를 누르지 않아도 시간만큼 플레이 후 종료)
  const [simpleMode, setSimpleMode] = useState(false);
  const [sessionSecondsLimit, setSessionSecondsLimit] = useState(0);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);
  const [includeTutorial, setIncludeTutorial] = useState(true);

  // 현재 문제 텍스트
  const [currentQuestion, setCurrentQuestion] = useState('');
  
  // 최종 결과 표시용 상태
  const [finalResults, setFinalResults] = useState(null);
  
  // 상세보기 모달 관련 상태
  const [showDetailModal, setShowDetailModal] = useState(false);

  // 모든 게임의 이미지를 한 번에 미리 로딩하는 함수
  const preloadAllGameImages = React.useCallback(() => {
    // 이미 로딩이 완료되었거나 진행 중이면 중복 실행 방지
    if (allImagesLoaded || allImagesLoading) {
      return;
    }

    // 이전 로딩이 진행 중이면 취소
    if (preloadCancelRef.current) {
      preloadCancelRef.current();
      preloadCancelRef.current = null;
    }

    
    // 모든 게임 타입의 이미지 URL 수집
    const allImageUrls = new Set(); // 중복 제거를 위해 Set 사용
    
    // 각 게임 타입별 이미지 추가
    ['bubbleWrap', 'balloonPop', 'robotFactory', 'eraseAndPick'].forEach(gameType => {
      const gameImages = getGameImageUrls(gameType, bubble, bubble_filled);
      gameImages.forEach(url => allImageUrls.add(url));
    });
    
    const imageUrlArray = Array.from(allImageUrls);
    
    setAllImagesLoading(true);
    setAllImagesProgress(0);
    setAllImagesLoaded(false);
    
    // 이미지 미리 로딩 시작
    const cancelPreload = preloadGameImages(
      imageUrlArray,
      (progress, loaded, total) => {
        setAllImagesProgress(progress);
      },
      () => {
        setAllImagesLoading(false);
        setAllImagesProgress(1);
        setAllImagesLoaded(true);
        preloadCancelRef.current = null;
      },
      (error, url) => {
        console.error(`❌ 게임 이미지 로딩 실패: ${url}`, error);
      }
    );
    
    preloadCancelRef.current = cancelPreload;
  }, [bubble, bubble_filled, allImagesLoaded, allImagesLoading]);

  // 페이지가 처음 로드될 때 커버 이미지와 모든 게임 이미지를 미리 불러옵니다.
  useEffect(() => {
    // 모든 게임 이미지 백그라운드 미리 로딩 시작
    preloadAllGameImages();
  }, [preloadAllGameImages]);

  // 색상 테마 변수들 (티처링 primary 색상 적용)
  const colors = {
    background: '#16bf9f', // 티처링 primary 색상
    text: '#374151',       // 어두운 회색 텍스트
    textSecondary: '#6b7280', // 보조 텍스트
    white: '#ffffff',      // 흰색
    border: '#d1d5db',     // 테두리
    success: '#10b981',    // 성공 (초록)
    error: '#ef4444',      // 오류 (빨강)
    warning: '#f59e0b',    // 경고 (노랑)
    info: '#3b82f6'        // 정보 (파랑)
  };




  // localStorage에서 학급 정보 가져오기
  const [classInfo, setClassInfo] = useState(null);
  const [classLoadError, setClassLoadError] = useState(null);
  const [isNewClassModeFromStorage, setIsNewClassModeFromStorage] = useState(false);

  useEffect(() => {
    const loadClassData = async () => {
      try {
        const storedData = localStorage.getItem('shortFormQuiz_selectedClass') || 
                          localStorage.getItem('marbleDrawing_selectedClass');
        
        if (storedData) {
          const parsed = JSON.parse(storedData);
          const { classId, isNewClassMode } = parsed;
          setClassInfo({ classId });
          setIsNewClassModeFromStorage(isNewClassMode || false);
        }
      } catch (error) {
        console.error('저장된 학급 데이터 파싱 실패:', error);
        setClassLoadError('저장된 학급 정보를 불러올 수 없습니다.');
      }
    };
    
    loadClassData();
  }, []);

  const classIdFromQuery = classInfo?.classId || null;

  // ===============================================
  // 🎮 게임 시퀀스 생성 (GAME_CONFIG 설정 기반)
  // ===============================================
  useEffect(() => {
    if (quiz?.questions) {
      let gameSequence = [];
      
      // 튜토리얼 포함 여부에 따라 시퀀스 생성
      if (includeTutorial) {
        // 튜토리얼 게임 4개 + 퀴즈 게임들 생성
        const tutorialGames = [
          getTutorialGame(1), // 1단계 튜토리얼
          getTutorialGame(2), // 2단계 튜토리얼
          getTutorialGame(3), // 3단계 튜토리얼
          getTutorialGame(4)  // 4단계 튜토리얼
        ];
        
        // 퀴즈 단계 게임들 생성
        const quizGames = generateQuizGameSequence(quiz.questions.length);
        
        gameSequence = [...tutorialGames, ...quizGames];
      } else {
        // 튜토리얼 없이 퀴즈 게임들만 생성
        gameSequence = generateQuizGameSequence(quiz.questions.length);
      }
      
      // 전체 게임 시퀀스 설정
      setGameSequence(gameSequence);
      
    }
  }, [quiz?.questions, includeTutorial]);

  // ===============================================
  // 🔧 현재 단계의 게임 컴포넌트 동적 선택
  // ===============================================
  const ActiveGameComponent = useMemo(() => {
    if (!gameSequence.length) return null;
    const gameId = gameSequence[localStage - 1];
    const component = getGameComponent(gameId);
    
    if (!component) {
      console.warn(`⚠️  게임 ID "${gameId}"에 해당하는 컴포넌트를 찾을 수 없습니다.`);
      return getGameComponent('BubbleWrap'); // 기본값으로 BubbleWrap 사용
    }
    
    return component;
  }, [localStage, gameSequence]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        let found = null;
        const [docId, uniqueQuizIdPart] = String(code).split('::');

        if (docId) {
          const connection = await checkFirebaseConnection();
          if (connection && connection.overall && db) {
            const collectionsToTry = ['shortForm_Quiz_member', 'shortForm_Quiz_guest'];
            for (const collectionName of collectionsToTry) {
              try {
                const docRef = doc(db, collectionName, docId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                  const data = docSnap.data();
                  const quizList = data.퀴즈List || [];
                  const targetQuiz = quizList.find(q => q.id === code);

                  if (targetQuiz) {
                    found = { ...targetQuiz, origin: collectionName === 'shortForm_Quiz_member' ? 'member' : 'guest' };
                    break;
                  }
                }
              } catch (error) {
                if (error.code === 'permission-denied') {
                  continue;
                }
                throw error;
              }
            }
          }
        }
        
        if (!found) {
          setError('퀴즈를 찾을 수 없습니다.');
        } else {
          setQuiz(found);
        }
      } catch (e) {
        setError('퀴즈 로딩 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code]);

  // 회원+학급 선택된 경우 학생명 드롭다운 로딩 (meta.classId 또는 URL classId 사용)
  // 단, 새 학급 모드(guest_로 시작하는 classId 또는 localStorage의 isNewClassMode)면 학생 목록을 로드하지 않음
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        if (!quiz) return;
        const params = new URLSearchParams(location.search);
        const classId = params.get('classId') || classIdFromQuery || quiz?.meta?.classId;
        const isMemberQuiz = quiz.origin === 'member';

        // 새 학급 모드 체크 (localStorage에서 명시적으로 설정되거나, guest_로 시작하거나 default인 경우)
        const isNewClassMode = isNewClassModeFromStorage || !classId || classId === 'default' || classId.startsWith('guest_');

        if (!isMemberQuiz || !classId || isNewClassMode) {
          setStudentNames([]);
          return;
        }
        setIsLoadingStudents(true);
        const result = await getAllStudentNames(classId);
        
        if (result.success) {
          setStudentNames(result.data);
        } else {
          setStudentNames([]);
        }
      } catch (error) {
        console.error('[학생 페이지] 학생 목록 로딩 중 오류:', error);
        setStudentNames([]);
      } finally {
        setIsLoadingStudents(false);
      }
    };
    fetchStudents();
  }, [quiz, classIdFromQuery, location.search, isNewClassModeFromStorage]);

  // 세션 상태 구독: 시작되면 대기 해제(향후 문제 화면으로 전환 예정)
  useEffect(() => {
    let unsub = null;
    const listenSession = async () => {
      if (!quiz?.id) return;
      const sessionCollectionName = quiz.origin === 'guest' 
        ? 'shortForm_Quiz_sessions_guest' 
        : 'shortForm_Quiz_sessions_member';
      const sessionDocRef = doc(db, sessionCollectionName, String(quiz.id));
      
      unsub = onSnapshot(sessionDocRef, (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const st = data?.session?.status || 'idle';
          setSessionStatus(st);
          const qLen = Array.isArray(quiz?.questions) ? quiz.questions.length : 0;
          const computedStages = Math.max(1, 2 + qLen);
          const sessionStages = Number(data?.session?.totalStages) || 0;
          if (sessionStages > 0) {
            setTotalStages(sessionStages);
          } else if (computedStages) {
            setTotalStages(computedStages);
          }
          if (data?.session?.currentStage) setCurrentStage(Number(data.session.currentStage));
          if (data?.session?.perStageSeconds) {
            const sec = Number(data.session.perStageSeconds);
            setPerStageSeconds(sec);
            setSessionSecondsLimit(sec);
          }
          if (typeof data?.session?.includeTutorial === 'boolean') {
            setIncludeTutorial(data.session.includeTutorial);
          }
          // 교사가 세션을 시작하지 않은 경우 단순 플레이 모드 활성화 -> 비활성화
          setSimpleMode(false);
          if (st === 'started' && phase === 'waiting') {
            // 🖼️ 모든 이미지가 로딩 완료되었는지 확인
            if (!allImagesLoaded) {
              return; // 이미지 로딩이 완료될 때까지 게임 시작 대기
            }
            
            // 스테이지 사이클 시작
            try {
              const qLenInit = Array.isArray(quiz?.questions) ? quiz.questions.length : 0;
              correctCountRef.current = 0;
              totalQuestionsRef.current = qLenInit; // 점수는 문제 수 기준(튜토리얼 제외)
              answersRef.current = Array.from({ length: qLenInit }, () => null);
              wrongIndexesRef.current = [];
            } catch (_) {}
            // 튜토리얼 포함 여부에 따라 시작 스테이지 결정
            const startStage = 1; // 항상 1단계부터 시작 (튜토리얼 포함/비포함 모두)
            setLocalStage(startStage);
            setStagePhase('pre');
            setPreLeft(2);
            // 시작 단계에 맞는 플레이 시간 설정
            setPlayLeft(getStagePlayTime(startStage) * 10);
            setEndLeft(2);
            
          }
        }
      });
    };
    listenSession();
    return () => { if (unsub) unsub(); };
  }, [quiz?.id, quiz?.origin, phase, allImagesLoaded]);

  // 응답 업데이트 또는 생성 함수
  const upsertResponse = useCallback(async (response) => {
    if (!sessionId || !quiz) return;
    try {
      const collectionName = quiz.origin === 'member' ? 'shortForm_Quiz_sessions_member' : 'shortForm_Quiz_sessions_guest';
      const sessionDocRef = doc(db, collectionName, sessionId);
      
      await setDoc(sessionDocRef, {
        responses: {
          [studentId]: response
        }
      }, { merge: true });

    } catch (e) {
      console.error('[학생 페이지] 응답 저장 중 오류:', e);
    }
  }, [sessionId, studentId, quiz]);

  // 종료 전송 함수(마지막 단계 종료 시)
  const sendFinishedFlag = async () => {
    try {
      await upsertResponse({
        name: (studentName || '').trim(),
        finished: true,
        submittedAt: serverTimestamp()
      });
    } catch (_) {}
  };

  // 단순 플레이 모드에서 시간 종료 시 최종 점수 전송
  const sendFinalScore = async () => {
    console.log("🔥🔥🔥 sendFinalScore 함수 호출됨! 🔥🔥🔥");
    try {
      // 실제 답안 배열을 기반으로 정확한 점수 계산
      const answers = Array.isArray(answersRef.current) ? answersRef.current : [];
      const questions = Array.isArray(quiz?.questions) ? quiz.questions : [];
      let actualCorrectCount = 0;
      const actualWrongIndexes = [];
      
      // 실제 정답 개수 및 틀린 문제 번호 계산
      for (let i = 0; i < Math.min(answers.length, questions.length); i++) {
        const selectedIndex = answers[i];
        const correctIndex = questions[i]?.answerIndex;
        if (typeof selectedIndex === 'number' && selectedIndex === correctIndex) {
          actualCorrectCount++;
        } else {
          // 답을 선택했지만 틀렸거나, 답을 선택하지 않은 경우 모두 틀린 것으로 처리
          actualWrongIndexes.push(i + 1); // 1-based 인덱스
        }
      }
      
      const results = {
        correct: actualCorrectCount,
        totalQuestions: totalQuestionsRef.current,
        answers: answers,
        wrongIndexes: actualWrongIndexes,
        name: (studentName || '').trim()
      };
      
      // 결과를 로컬 상태에 저장하여 표시
      setFinalResults(results);
      
      // upsertResponse 함수를 사용하여 기존 응답 업데이트 또는 새로 생성
      await upsertResponse({
        ...results,
        submittedAt: serverTimestamp()
      });

      // ----------------- 누가기록 저장 로직 추가 -----------------
      const params = new URLSearchParams(location.search);
      const classId = params.get('classId');
      const teacherId = quiz.meta?.ownerUid; // quiz.meta 객체 안에 ownerUid가 저장되어 있음
      
      console.log(`📋 누가기록 저장 조건 확인: classId='${classId}', teacherId='${teacherId}'`);

      if (classId && teacherId) {
        console.log("✅ 조건 통과! 누가기록 저장을 시작합니다.");
        // 1. 현재 학생의 studentId 찾기
        const studentsResult = await getAllStudentNames(classId);
        let currentStudent = null;
        if (studentsResult.success) {
          currentStudent = studentsResult.data.find(s => s.name === results.name);
        }

        if (currentStudent) {
          const activityId = `shortform_${quiz.id}`;
          
          // 선택된 학생 ID가 있으면 사용하고, 없으면 이름으로 찾은 학생 ID 사용
          const targetStudentId = selectedStudentId || currentStudent.studentId;
          
          // 점수 계산
          const score = results.totalQuestions > 0 ? Math.round((results.correct / results.totalQuestions) * 100) : 0;
          
          // 맞춘 문제 정보 추출
          const correctQuestions = [];
          if (quiz.questions && Array.isArray(quiz.questions) && results.answers && Array.isArray(results.answers)) {
            quiz.questions.forEach((question, index) => {
              const answer = results.answers[index];
              if (answer && answer.isCorrect) {
                correctQuestions.push({
                  content: question.question || question.text || `문제 ${index + 1}`,
                  category: question.category || question.type || '',
                  difficulty: question.difficulty || ''
                });
              }
            });
          }
          
          // AI로 누가기록 생성
          let teacherComment = `${results.correct}/${results.totalQuestions} 정답 (${score}점)`;
          try {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            const userId = currentUser?.uid || teacherId || 'anonymous';

            const aiResult = await shortFormCumulativeRecordGenerator.generateRecord({
              quizData: {
                quizTitle: quiz.title || '숏폼 퀴즈',
                quizDescription: '',  // 숏폼 퀴즈는 설명이 없음
                score: score,
                correctCount: results.correct,
                totalQuestions: results.totalQuestions,
                correctQuestions: correctQuestions
              },
              studentInfo: {
                name: results.name,
                class: ''
              },
              userId: userId,
              isProUser: false
            });

            if (aiResult.success && aiResult.record) {
              teacherComment = aiResult.record;
              console.log('✅ AI 누가기록 생성 성공:', teacherComment);
            } else {
              console.warn('⚠️ AI 누가기록 생성 실패, 기본 코멘트 사용:', aiResult.error);
            }
          } catch (aiError) {
            console.error('❌ AI 누가기록 생성 오류:', aiError);
          }
          
          // 2. 학생 개별 기록 데이터 구성 및 저장
          const studentRecordData = {
            activityInstanceId: activityId,
            activityType: "숏폼퀴즈",
            classId: classId,
            teacherId: teacherId,
            timestamp: serverTimestamp(),
            teacherComment: teacherComment
          };
          await addShortFormQuizActivityRecord(classId, targetStudentId, activityId, studentRecordData);

          // 3. 학급 전체 활동 기록 업데이트/생성
          const studentResponseData = {
            studentId: targetStudentId,
            studentName: results.name,
            correct: results.correct,
            totalQuestions: results.totalQuestions,
            wrongIndexes: results.wrongIndexes || [],
            completedAt: new Date().toISOString(), // serverTimestamp()는 배열 안에 사용 불가
          };
          
          await upsertShortFormQuizClassActivity(
            classId,
            activityId,
            quiz.title,
            teacherId,
            quiz.id,
            studentResponseData
          );
        }
      }
      // ----------------- 누가기록 저장 로직 종료 -----------------

    } catch (_) {}
  };

  // 각 단계별 플레이 시간 계산 함수
  const getStagePlayTime = (stage) => {
    if (includeTutorial && stage <= 4) {
      // 튜토리얼 포함이고 1, 2, 3, 4단계: 5초
      return 5;
    } else {
      // 튜토리얼 없거나 퀴즈 문제 단계: 선생님이 설정한 시간 사용
      return perStageSeconds || 10; // 기본값 10초
    }
  };

  // 단순 플레이 모드 글로벌 타이머 (세션 전체 시간 카운트)
  useEffect(() => {
    if (!simpleMode) return;
    if (phase !== 'waiting') return;
    if (!sessionTimeLeft || sessionTimeLeft <= 0) return;
    const id = setInterval(() => {
      setSessionTimeLeft((v) => {
        if (v <= 1) {
          clearInterval(id);
          // 현재 게임이 진행 중이면 게임 완료까지 대기
          if (isGameInProgress) {
            // 게임이 끝날 때까지 대기하고, 게임이 끝나면 finalize 실행
            return 0; // 타이머는 0으로 설정하지만 게임은 계속 진행
          } else {
            // 게임이 진행 중이 아니면 즉시 종료
            const finalize = async () => {
              try {
                await sendFinalScore();
                await sendFinishedFlag();
              } catch (_) {}
            };
            finalize();
            setStagePhase('idle');
            setPhase('final');
            return 0;
          }
        }
        return v - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [simpleMode, phase, sessionTimeLeft, isGameInProgress]);

  // pre → play → feedback(선택 시) → end 타이머 사이클
  useEffect(() => {
    if (phase !== 'waiting') return;
    let timerId = null;
    if (stagePhase === 'pre') {
      // 모든 문제가 완료되었는지 먼저 확인
      const tutorialStages = includeTutorial ? 4 : 0;
      const actualTotalStages = tutorialStages + (Array.isArray(quiz?.questions) ? quiz.questions.length : 0);
      if (localStage > actualTotalStages) {
        // 모든 문제 완료 시 즉시 종료
        const finalize = async () => {
          try {
            await sendFinalScore();
            await sendFinishedFlag();
          } catch (_) {}
        };
        finalize();
        setStagePhase('idle');
        setPhase('final');
        return;
      }
      
      timerId = setInterval(() => {
        setPreLeft((v) => {
          if (v <= 1) {
            clearInterval(timerId);
            try { stageAnsweredRef.current = false; } catch (_) {}
            // 플레이 단계 시작 시 게임 진행 상태로 설정
            setIsGameInProgress(true);
            

            
            setStagePhase('play');
            return 0;
          }
          return v - 1;
        });
      }, 1000);
    } else if (stagePhase === 'play') {
      // 모든 문제가 완료되었는지 먼저 확인
      const tutorialStages = includeTutorial ? 4 : 0;
      const actualTotalStages = tutorialStages + (Array.isArray(quiz?.questions) ? quiz.questions.length : 0);
      if (localStage > actualTotalStages) {
        // 모든 문제 완료 시 즉시 종료
        const finalize = async () => {
          try {
            await sendFinalScore();
            await sendFinishedFlag();
          } catch (_) {}
        };
        finalize();
        setStagePhase('idle');
        setPhase('final');
        return;
      }
      
      timerId = setInterval(() => {
        // 이미 답변이 선택되었거나 피드백 단계로 전환된 경우 타이머 중지
        if (stageAnsweredRef.current || stagePhase === 'feedback') {
          clearInterval(timerId);
          return;
        }
        
        setPlayLeft((v) => {
          if (v <= 1) {
            clearInterval(timerId);
            
            // 마지막 순간에 답변이 선택되었는지 다시 한번 확인
            if (stageAnsweredRef.current || stagePhase === 'feedback') {
              return 0;
            }
            
            // 시간 종료 시 점수 처리
            try {
              const tutorialStages = includeTutorial ? 4 : 0;
              const isQuizStage = localStage > tutorialStages;
              if (isQuizStage && !stageAnsweredRef.current) {
                // 미응답은 오답으로만 기록 (총 문항 수는 고정 값)
                const qList = Array.isArray(quiz?.questions) ? quiz.questions : [];
                const qIndexRaw = Math.max(0, localStage - tutorialStages - 1);
                // 순환 참조 제거: 실제 문제 범위 내에서만 처리
                if (qIndexRaw < qList.length) {
                  if (!Array.isArray(wrongIndexesRef.current)) wrongIndexesRef.current = [];
                  const oneBased = qIndexRaw + 1;
                  if (!wrongIndexesRef.current.includes(oneBased)) wrongIndexesRef.current.push(oneBased);
                }
              }
            } catch (_) {}
            
            // 시간 초과 시 오답 피드백 표시
            const cw = canvasRef.current?.width || 600;
            const ch = canvasRef.current?.height || 400;
            const base = Math.min(cw, ch) * 0.3;
            setFeedbackActive(true);
            setFeedbackIsSuccess(false);
            setFeedbackRect({ x: cw / 2, y: ch / 2, w: base, h: base });
            setStagePhase('feedback');
            
            // 플레이 단계 종료 시 게임 진행 상태 해제
            setIsGameInProgress(false);
            // simpleMode이고 전체 시간이 종료되었으면 즉시 종료
            if (simpleMode && sessionTimeLeft <= 0) {
              const finalize = async () => {
                try {
                  await sendFinalScore();
                  await sendFinishedFlag();
                } catch (_) {}
              };
              finalize();
              setStagePhase('idle');
              setPhase('final');
            } else {
              // 그 외의 경우 (교사모드 전체, 또는 simpleMode 시간 남음)는 피드백으로
              const cw = canvasRef.current?.width || 600;
              const ch = canvasRef.current?.height || 400;
              const base = Math.min(cw, ch) * 0.3;
              setFeedbackActive(true);
              setFeedbackIsSuccess(false);
              setFeedbackRect({ x: cw / 2, y: ch / 2, w: base, h: base });
              setStagePhase('feedback');
            }
            return 0;
          }
          return v - 1;
        });
      }, 100);
    } else if (stagePhase === 'feedback') {
      // 2초 피드백 후 자동으로 다음 단계 준비 또는 종료
      timerId = setTimeout(() => {
        setFeedbackActive(false);
        setFeedbackRect(null);
        // 피드백 종료 시 게임 진행 상태 해제 (이미 답안 선택 시 해제되었지만 안전장치)
        setIsGameInProgress(false);

        // 모든 문제를 풀었는지 확인 (튜토리얼 포함)
        const tutorialStages = includeTutorial ? 4 : 0;
        const actualTotalStages = tutorialStages + (Array.isArray(quiz?.questions) ? quiz.questions.length : 0);
        if (localStage > actualTotalStages) {
          // 퀴즈 종료, 결과 화면으로 이동
          const finalize = async () => {
            try {
              await sendFinalScore();
              await sendFinishedFlag();
            } catch (_) {}
          };
          finalize();
          setStagePhase('idle');
          setPhase('final');
        } else {
          // 다음 문제로 이동하기 전에 다시 한번 확인
          setLocalStage((s) => {
            const nextStage = s + 1;
            const tutorialStages = includeTutorial ? 4 : 0;
            const actualTotalStages = tutorialStages + (Array.isArray(quiz?.questions) ? quiz.questions.length : 0);
            
            // 다음 스테이지가 총 단계를 초과하면 즉시 종료
            if (nextStage > actualTotalStages) {
              const finalize = async () => {
                try {
                  await sendFinalScore();
                  await sendFinishedFlag();
                } catch (_) {}
              };
              finalize();
              setStagePhase('idle');
              setPhase('final');
              return s; // 현재 스테이지 유지
            }
            
            // 다음 단계의 플레이 시간 설정
            setPlayLeft(getStagePlayTime(nextStage) * 10);
            return nextStage;
          });
          setPreLeft(2);
          setEndLeft(2);
          setStagePhase('pre');
        }
      }, 2000);
    } else if (stagePhase === 'end') {
      timerId = setInterval(() => {
        setEndLeft((v) => {
          if (v <= 1) {
            clearInterval(timerId);
            // 마지막 단계 여부 확인 후 다음 단계 또는 종료 표시
            if (simpleMode) {
              const tutorialStages = includeTutorial ? 4 : 0;
              const actualTotalStages2 = tutorialStages + (Array.isArray(quiz?.questions) ? quiz.questions.length : 0);
              if (localStage > actualTotalStages2) {
                // 퀴즈 종료, 결과 화면으로 이동
                const finalize = async () => {
                  try {
                    await sendFinalScore();
                    await sendFinishedFlag();
                  } catch (_) {}
                };
                finalize();
                setStagePhase('idle');
                setPhase('final');
              } else {
                // 다음 문제로 이동하기 전에 다시 한번 확인
                setLocalStage((s) => {
                  const nextStage = s + 1;
                  const tutorialStages = includeTutorial ? 4 : 0;
                  const actualTotalStages = tutorialStages + (Array.isArray(quiz?.questions) ? quiz.questions.length : 0);
                  
                  // 다음 스테이지가 총 단계를 초과하면 즉시 종료
                  if (nextStage > actualTotalStages) {
                    const finalize = async () => {
                      try {
                        await sendFinalScore();
                        await sendFinishedFlag();
                      } catch (_) {}
                    };
                    finalize();
                    setStagePhase('idle');
                    setPhase('final');
                    return s; // 현재 스테이지 유지
                  }
                  
                  // 다음 단계의 플레이 시간 설정
                  setPlayLeft(getStagePlayTime(nextStage) * 10);
                  return nextStage;
                });
                setPreLeft(2);
                setEndLeft(2);
                setStagePhase('pre');
              }
            } else {
              setStagePhase('idle');
              // 최종 종료 플래그 전송 후 전체 결과 화면으로 전환
              sendFinishedFlag();
              setPhase('final');
            }
            return 0;
          }
          return v - 1;
        });
      }, 1000);
    }
    return () => { if (timerId) clearInterval(timerId); };
  }, [stagePhase, phase, localStage, totalStages, perStageSeconds, simpleMode, includeTutorial, sessionTimeLeft]);

  // 기존 waiting 단계에서의 이미지 로딩 코드는 페이지 로드 시로 이동됨 (위의 useEffect에서 처리)

  // 이미지 로딩 완료 시 대기 중인 게임을 시작
  React.useEffect(() => {
    if (allImagesLoaded && phase === 'waiting' && sessionStatus === 'started') {
      
      // 게임 시작 조건 재확인 및 시작
      try {
        const qLenInit = Array.isArray(quiz?.questions) ? quiz.questions.length : 0;
        correctCountRef.current = 0;
        totalQuestionsRef.current = qLenInit;
        answersRef.current = Array.from({ length: qLenInit }, () => null);
        wrongIndexesRef.current = [];
        
        const startStage = 1;
        setLocalStage(startStage);
        setStagePhase('pre');
        setPreLeft(2);
        setPlayLeft(getStagePlayTime(startStage) * 10);
        setEndLeft(2);
        
      } catch (error) {
        console.error('게임 시작 중 오류:', error);
      }
    }
  }, [allImagesLoaded, phase, sessionStatus, quiz?.questions]);

  // 컴포넌트 언마운트 시 이미지 미리 로딩 취소
  React.useEffect(() => {
    return () => {
      if (preloadCancelRef.current) {
        preloadCancelRef.current();
        preloadCancelRef.current = null;
      }
    };
  }, []);



  // 현재 스테이지에 맞는 문제 텍스트 설정
  useEffect(() => {
    if (phase !== 'waiting') {
      setCurrentQuestion('');
      return;
    }

    // 튜토리얼 단계인지 확인 (includeTutorial이 true이고 1-4단계)
    const isTutorialStage = includeTutorial && localStage >= 1 && localStage <= 4;
    
    if (isTutorialStage) {
      // 튜토리얼 단계 1, 2, 3, 4
      const gameId = gameSequence[localStage - 1];
      const message = getTutorialMessage(gameId);
      setCurrentQuestion(message);
    } else if (quiz?.questions) {
      // 퀴즈 문제 단계
      const qList = quiz.questions;
      let qIndexRaw;
      
      const tutorialStages = includeTutorial ? 4 : 0;
      // 튜토리얼 단계 수를 고려하여 문제 인덱스 계산
      qIndexRaw = localStage - tutorialStages - 1;
      
      // 유효한 문제 범위 확인
      if (qIndexRaw >= 0 && qIndexRaw < qList.length) {
        const q = qList[qIndexRaw];
        if (q && q.question) {
          setCurrentQuestion(q.question);
        } else {
          // question 필드가 없는 경우 대비
          setCurrentQuestion(`문제 ${qIndexRaw + 1}`);
        }
      } else if (qIndexRaw >= qList.length) {
        // 모든 문제를 다 푼 경우
        setCurrentQuestion('모든 문제를 완료했습니다!');
      } else {
        setCurrentQuestion('');
      }
    } else {
      setCurrentQuestion('');
    }
  }, [localStage, phase, quiz?.questions, gameSequence, includeTutorial]);

  const handleJoin = async () => {
    const name = (studentName || '').trim();
    if (!name) {
      alert('이름을 입력하거나 선택해주세요.');
      return;
    }
    setIsJoining(true);
    try {
      // 1. 세션 ID와 학생 ID를 생성하고 상태에 저장합니다.
      const currentSessionId = quiz.id;
      // 학급에서 선택한 경우 고유 studentId를 사용하고, 직접 입력한 경우 이름 기반으로 생성합니다.
      const newStudentId = selectedStudentId || `${name}_${Date.now()}`;
      setSessionId(currentSessionId);
      setStudentId(newStudentId);

      // 2. 이후 재접속을 위해 학생 이름을 로컬에 저장합니다.
      localStorage.setItem(`shortform_student_name_${code}`, name);

      // 3. 세션 문서에 학생 참여 정보를 기록합니다. (upsertResponse와 동일한 로직)
      const connection = await checkFirebaseConnection();
      if (connection && connection.overall && db && quiz?.id) {
        const sessionCollectionName = quiz.origin === 'member' ? 'shortForm_Quiz_sessions_member' : 'shortForm_Quiz_sessions_guest';
        const sessionDocRef = doc(db, sessionCollectionName, currentSessionId);

        const participantData = {
          name,
          studentId: newStudentId,
          joinedAt: serverTimestamp(),
        };
        
        // setDoc + merge: true를 사용하여 세션 문서에 이 학생의 정보를 추가/업데이트합니다.
        await setDoc(sessionDocRef, {
          responses: {
            [newStudentId]: participantData
          }
        }, { merge: true });
      }

      // 4. 대기 화면으로 전환합니다.
      setPhase('waiting');

      // (기존 simpleMode 로직은 유지)
      if (simpleMode) {
        try {
          const qLenInit = Array.isArray(quiz?.questions) ? quiz.questions.length : 0;
          correctCountRef.current = 0;
          totalQuestionsRef.current = qLenInit;
          answersRef.current = Array.from({ length: qLenInit }, () => null);
          wrongIndexesRef.current = [];
        } catch (_) {}
        const startStage = 1;
        setLocalStage(startStage);
        const limit = (sessionSecondsLimit && sessionSecondsLimit > 0) ? sessionSecondsLimit : (perStageSeconds || 30);
        setSessionTimeLeft(limit);
        setPlayLeft(getStagePlayTime(startStage) * 10);
        setPreLeft(2);
        setEndLeft(0);
        setStagePhase('pre');
      }
    } catch (e) {
      console.error("참여 처리 중 심각한 오류 발생:", e);
      alert('참가 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsJoining(false);
    }
  };

  if (loading) {
    return (
      <section className="content-start-section"><div className="container"><div className="tools-frame-content" style={{ textAlign: 'center' }}>불러오는 중...</div></div></section>
    );
  }
  if (error) {
    return (
      <section className="content-start-section"><div className="container"><div className="tools-frame-content" style={{ color: 'var(--danger, #dc3545)' }}>{error}</div></div></section>
    );
  }

  return (
    <section className="content-start-section student-page-layout">
      <div className="container">
        <div className="tools-frame-content" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'transparent',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '16px'
        }}>
          <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            maxWidth: `calc(100vh * 9 / 16)`,
            maxHeight: `calc(100vw * 16 / 9)`,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {phase === 'join' ? (
              <GameScreenLayout backgroundColor={colors.background}>
                {/* 상단 여백 (문제/타이머 UI 공간) */}
                <div style={{ height: '120px', flexShrink: 0 }} />

                {/* 실제 참여 UI (나머지 공간 차지 및 중앙 정렬) */}
                <div style={{
                  flex: 1,
                  display: 'grid',
                  placeContent: 'center',
                  gap: 'var(--spacing-xl)',
                  color: colors.text,
                  padding: '0 32px 48px 32px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <h2 className="section-title" style={{ margin: 0, color: colors.white, fontSize: '2.5rem' }}>{quiz?.title || '퀴즈 참가'}</h2>

                  </div>

                  <div style={{ display: 'grid', gap: 'var(--spacing-sm)', justifyItems: 'center' }}>
                    {/* 이미지 로딩 진행률 표시 */}
                    {(allImagesLoading || (!allImagesLoaded && allImagesProgress > 0)) && (
                      <div style={{
                        width: '100%',
                        maxWidth: 360,
                        marginBottom: '16px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '14px',
                          color: colors.white,
                          marginBottom: '8px',
                          opacity: 0.9
                        }}>
                          게임 이미지 로딩 중... {Math.round(allImagesProgress * 100)}%
                        </div>
                        <div style={{
                          width: '100%',
                          height: '6px',
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            height: '100%',
                            backgroundColor: colors.white,
                            borderRadius: '3px',
                            width: `${allImagesProgress * 100}%`,
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                    )}

                    {/* 이미지 로딩 완료 표시 */}
                    {allImagesLoaded && (
                      <div style={{
                        width: '100%',
                        maxWidth: 360,
                        marginBottom: '12px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '14px',
                          color: colors.white,
                          opacity: 0.9,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}>
                          <span>✅</span>
                          <span>게임 이미지 로딩 완료</span>
                        </div>
                      </div>
                    )}

                    {isLoadingStudents ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        maxWidth: 360,
                        height: '45px',
                        color: colors.textSecondary,
                        fontSize: '1.125rem'
                      }}>
                        입장 중...
                      </div>
                    ) : studentNames && studentNames.length > 0 ? (
                      <div style={{ position: 'relative', width: '100%', maxWidth: 360 }}>
                        <div style={{ maxWidth: '360px' }}>
                          <AlwaysDownSelect
                            options={studentNames.map(s => ({ 
                              value: s.name, 
                              label: (s.studentNumber ? `${s.studentNumber}번 ` : '') + s.name,
                              studentId: s.studentId // studentId 정보 포함
                            }))}
                            value={studentName}
                            onChange={(val) => {
                              setStudentName(val);
                              // 선택된 학생의 studentId 찾아서 저장
                              const selectedStudent = studentNames.find(s => s.name === val);
                              if (selectedStudent) {
                                setSelectedStudentId(selectedStudent.studentId);
                              } else {
                                setSelectedStudentId(null);
                              }
                            }}
                            isDisabled={isLoadingStudents}
                            placeholder="학생을 선택하세요"
                          />
                        </div>
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={studentName}
                        onChange={(e) => setStudentName(e.target.value)}
                        placeholder="이름을 입력하세요"
                        style={{
                          width: '100%',
                          maxWidth: 360,
                          border: `2px solid ${colors.border}`,
                          borderRadius: 8,
                          padding: '10px 12px',
                          backgroundColor: colors.white,
                          color: colors.text,
                          textAlign: 'center',
                          fontSize: '1.125rem'
                        }}
                      />
                    )}
                  </div>

                                     <div style={{ justifySelf: 'center' }}>
                     <button 
                       onClick={handleJoin} 
                       disabled={isJoining || isLoadingStudents || !studentName.trim()}
                       style={{
                         backgroundColor: colors.white,
                         color: colors.background,
                         border: 'none',
                         borderRadius: '12px',
                         padding: '12px 24px',
                         fontSize: '1.125rem',
                         fontWeight: '600',
                         cursor: isJoining || isLoadingStudents || !studentName.trim() ? 'not-allowed' : 'pointer',
                         transition: 'all 0.2s ease',
                         opacity: isJoining || isLoadingStudents || !studentName.trim() ? 0.6 : 1,
                         minWidth: '140px',
                         boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                       }}
                       onMouseEnter={(e) => {
                         if (!e.target.disabled) {
                           e.target.style.transform = 'translateY(-2px)';
                           e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.25)';
                         }
                       }}
                       onMouseLeave={(e) => {
                         if (!e.target.disabled) {
                           e.target.style.transform = 'translateY(0)';
                           e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                         }
                       }}
                     >
                       {isJoining ? '참가 중...' : (isLoadingStudents ? '학생 목록 로딩 중...' : '참가하기')}
                     </button>
                   </div>
                </div>
              </GameScreenLayout>
            ) : phase === 'waiting' ? (
              <GameScreenLayout backgroundColor={colors.background}>
                <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex:1 }}>
                  {/* 상단 UI (오버레이) */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    height: '20%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '1vmin',
                    color: colors.text,
                    padding: '1vmin 4vmin'
                  }}>
                    {/* 문제 텍스트 (플레이/피드백 단계에서만 표시) */}
                    <div style={{
                      fontSize: '3vmin',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      lineHeight: '1.4',
                      width: '100%',
                      flex: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      visibility: (stagePhase === 'play' || stagePhase === 'feedback') ? 'visible' : 'hidden',
                      color: 'white',
                      zIndex: 20,
                      whiteSpace: 'pre-line'
                    }}>
                      {currentQuestion}
                    </div>
                    
                    {/* 타이머 및 스테이지 정보 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      gap: '2vmin'
                    }}>
                      {/* 타이머 아이콘과 바는 play 또는 feedback 단계에서만 표시 */}
                      {(stagePhase === 'play' || stagePhase === 'feedback') && (
                        <>
                          <AlarmClock 
                            size={'5vmin'} 
                            color="white"
                            style={{
                              zIndex: 20
                            }}
                          />
                          <div style={{
                            position: 'relative',
                            width: '90%',
                            maxWidth: '400px',
                            height: '3vmin',
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            borderRadius: '1.5vmin',
                            overflow: 'hidden',
                            border: `0.2vmin solid white`,
                            zIndex: 20
                          }}>
                            <div style={{
                              height: '100%',
                              backgroundColor: colors.error, // 항상 빨간색으로 유지
                              borderRadius: '1.5vmin 0 0 1.5vmin',
                              transition: stagePhase === 'play' ? 'width 0.1s linear' : 'none',
                              width: `${(playLeft / (getStagePlayTime(localStage) * 10)) * 100}%` // 남은 시간 비율
                            }} />
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '2vmin'
                            }}>
                              {`${Math.ceil(playLeft / 10)}초`}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* 메인 콘텐츠 영역 */}
                  <div style={{ width: '100%', flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {/* 게임 컴포넌트는 플레이/피드백 단계에서만 렌더링하되, 모든 문제가 완료되었으면 렌더링하지 않음 */}
                    {(() => {
                      // 모든 문제가 완료되었는지 확인
                      const tutorialStages = includeTutorial ? 4 : 0;
                      const actualTotalStages = tutorialStages + (Array.isArray(quiz?.questions) ? quiz.questions.length : 0);
                      const isAllCompleted = localStage > actualTotalStages;
                      
                      return (stagePhase === 'play' || stagePhase === 'feedback') && 
                             ActiveGameComponent && 
                             !isAllCompleted && (
                        <ActiveGameComponent
                          stagePhase={stagePhase}
                          localStage={localStage}
                          quiz={quiz}
                          setStagePhase={setStagePhase}
                          correctCountRef={correctCountRef}
                          totalQuestionsRef={totalQuestionsRef}
                          answersRef={answersRef}
                          wrongIndexesRef={wrongIndexesRef}
                          stageAnsweredRef={stageAnsweredRef}
                          feedbackActive={feedbackActive}
                          feedbackIsSuccess={feedbackIsSuccess}
                          feedbackRect={feedbackRect}
                          setFeedbackActive={setFeedbackActive}
                          setFeedbackIsSuccess={setFeedbackIsSuccess}
                          setFeedbackRect={setFeedbackRect}
                          setIsGameInProgress={setIsGameInProgress}
                          canvasRef={canvasRef}
                          currentQuestion={currentQuestion}
                          bubbleImage={bubble}
                          bubbleFilledImage={bubble_filled}
                          // 튜토리얼/퀴즈 구분을 위한 추가 props
                          includeTutorial={includeTutorial}
                          isTutorialStage={includeTutorial && localStage >= 1 && localStage <= 4}
                        />
                      );
                    })()}
                    {/* 준비 단계 UI */}
                    {stagePhase === 'pre' && (
                      <div style={{
                        color: 'white',
                        padding: '32px',
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '24px', color: 'white' }}>
                            {(() => {
                              if (!gameSequence.length) return '준비하세요!';
                              const gameId = gameSequence[localStage - 1];
                              return getReadyMessage(gameId);
                            })()}
                          </div>
                          <div style={{ fontSize: '4rem', fontWeight: 'bold', color: 'white', margin: '24px 0' }}>
                            {preLeft}초
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
                            {[0, 1, 2].map((i) => (
                              <div
                                key={i}
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  backgroundColor: colors.textSecondary,
                                  animation: `pulse 1.5s ease-in-out ${i * 0.3}s infinite`
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* 대기 화면 (idle, end) 및 모든 문제 완료 화면 */}
                    {(() => {
                      const tutorialStages = includeTutorial ? 4 : 0;
                      const actualTotalStages = tutorialStages + (Array.isArray(quiz?.questions) ? quiz.questions.length : 0);
                      const isAllCompleted = localStage > actualTotalStages;
                      
                      // 모든 문제가 완료되었고 플레이/피드백 단계에서는 완료 메시지 표시
                      if (isAllCompleted && (stagePhase === 'play' || stagePhase === 'feedback')) {
                        return (
                          <div style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: colors.text,
                            padding: '32px'
                          }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '16px', color: colors.success }}>
                                🎉 모든 문제 완료!
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      // 기존 idle, end 화면
                      return (stagePhase === 'idle' || stagePhase === 'end') && (
                        <div style={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: colors.text,
                          padding: '32px'
                        }}>
                          {stagePhase === 'idle' && sessionStatus === 'started' && (
                            <div style={{ textAlign: 'center' }}>
                              {!allImagesLoaded ? (
                                // 이미지 로딩 중
                                <>
                                  <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '16px', color: 'white' }}>
                                    게임 준비 중...
                                  </div>
                                  <div style={{ fontSize: '1.125rem', opacity: 0.7 }}>
                                    모든 게임 이미지를 로딩하고 있습니다
                                  </div>
                                  <div style={{ fontSize: '1rem', marginTop: '12px', opacity: 0.8, color: 'white' }}>
                                    진행률: {Math.round(allImagesProgress * 100)}%
                                  </div>
                                  <div style={{ fontSize: '0.9rem', marginTop: '8px', opacity: 0.6, color: 'white' }}>
                                    잠시만 기다려주세요...
                                  </div>
                                </>
                              ) : (
                                // 이미지 로딩 완료, 게임 시작 준비
                                <>
                                  <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '16px', color: '#10b981' }}>
                                    준비 완료!
                                  </div>
                                  <div style={{ fontSize: '1.125rem', opacity: 0.8, color: 'white' }}>
                                    ✅ 모든 이미지 로딩 완료
                                  </div>
                                  <div style={{ fontSize: '1rem', marginTop: '8px', opacity: 0.7, color: 'white' }}>
                                    곧 게임이 시작됩니다...
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                          {stagePhase === 'idle' && sessionStatus !== 'started' && (
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '16px', color: 'white' }}>
                                선생님의 지시를 기다리는 중...
                              </div>
                              <div style={{ fontSize: '1.125rem', opacity: 0.7, color: 'white' }}>
                                잠시만 기다려주세요
                              </div>
                            </div>
                          )}
                          {stagePhase === 'end' && (
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '16px' }}>
                                완료!
                              </div>
                              <div style={{ fontSize: '1.5rem', opacity: 0.7 }}>
                                {(() => {
                                  const tutorialStages = includeTutorial ? 4 : 0;
                                  const actualTotalStages3 = tutorialStages + (Array.isArray(quiz?.questions) ? quiz.questions.length : 0);
                                  return localStage >= actualTotalStages3 ? '모든 문제를 완료했습니다!' : '다음 문제를 준비하고 있습니다...';
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </GameScreenLayout>
            ) : (
              // 전체 결과 화면
              <GameScreenLayout backgroundColor={colors.background}>
                {finalResults ? (
                  // 결과가 있을 때 - 점수 표시
                  <div style={{ display: 'grid', gap: 'var(--spacing-xl)', textAlign: 'center', color: colors.text, padding: '32px' }}>
                    <div>
                      <h2 style={{ margin: 0, color: colors.white, fontSize: '2.5rem', marginBottom: '16px' }}>퀴즈 완료!</h2>
                    </div>
                    
                    <div style={{ display: 'grid', gap: '24px', justifyItems: 'center' }}>
                      <div style={{ 
                        backgroundColor: colors.white, 
                        borderRadius: '12px', 
                        padding: '24px', 
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        minWidth: '300px'
                      }}>
                        <div style={{ fontSize: '16px', color: colors.textSecondary, fontWeight: 600, marginBottom: '8px' }}>참가자 이름</div>
                        <div style={{ fontSize: '24px', fontWeight: 800, color: colors.text, marginBottom: '16px' }}>{finalResults.name}</div>
                        
                        <div style={{ fontSize: '18px', color: colors.textSecondary, fontWeight: 600, marginBottom: '8px' }}>점수</div>
                        <div style={{ 
                          fontSize: '3rem', 
                          fontWeight: 'bold', 
                          color: finalResults.correct === finalResults.totalQuestions ? colors.success : colors.info,
                          marginBottom: '8px'
                        }}>
                          {finalResults.correct}/{finalResults.totalQuestions}
                        </div>
                        
                        <div style={{ 
                          fontSize: '1.25rem', 
                          color: colors.textSecondary,
                          marginBottom: '16px'
                        }}>
                          정답률: {finalResults.totalQuestions > 0 ? Math.round((finalResults.correct / finalResults.totalQuestions) * 100) : 0}%
                        </div>
                        
                        {finalResults.correct === finalResults.totalQuestions && (
                          <div style={{ 
                            fontSize: '1.125rem', 
                            color: colors.success, 
                            fontWeight: 'bold',
                            backgroundColor: '#f0fdf4',
                            padding: '8px 16px',
                            borderRadius: '8px'
                          }}>
                            🎉 완벽합니다!
                          </div>
                        )}
                        
                        {finalResults.wrongIndexes && finalResults.wrongIndexes.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <div style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '8px' }}>
                              틀린 문제: {finalResults.wrongIndexes.join(', ')}번
                            </div>
                          </div>
                        )}
                        
                        {/* 상세보기 버튼 */}
                        <div style={{ marginTop: '16px' }}>
                          <button
                            onClick={() => setShowDetailModal(true)}
                            style={{
                              padding: '12px 24px',
                              backgroundColor: colors.info,
                              color: colors.white,
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#2563eb';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = colors.info;
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            내 답안 상세보기
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // 결과가 아직 없을 때 - 집계 중 화면
                  <div style={{ display: 'grid', gap: 'var(--spacing-xl)', textAlign: 'center', color: colors.text }}>
                    <div>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '16px' }}>결과 집계 중...</div>
                      <p style={{ margin: 0, color: colors.textSecondary, fontSize: '1.125rem' }}>결과가 집계되는 동안 잠시만 기다려 주세요.</p>
                    </div>
                    <div style={{ display: 'grid', gap: 8, justifyItems: 'center' }}>
                      <div style={{ fontSize: 16, color: colors.textSecondary, fontWeight: 600 }}>참가자 이름</div>
                      <div style={{ fontSize: 28, fontWeight: 800, color: colors.text }}>{studentName}</div>
                    </div>
                    {/* 로딩 애니메이션 */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: colors.textSecondary,
                            animation: `pulse 1.5s ease-in-out ${i * 0.3}s infinite`
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </GameScreenLayout>
            )}
          </div>
        </div>
      </div>

      {/* 답안 상세보기 모달 */}
      {showDetailModal && finalResults && (
        <div
          style={{
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%',
            background: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1050
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowDetailModal(false); }}
        >
          <div style={{ 
            background: '#fff', 
            borderRadius: 12, 
            width: 'min(980px, 94%)', 
            maxHeight: '88%', 
            overflow: 'auto', 
            padding: 20 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>{finalResults.name}의 답안</h3>
              <button 
                className="btn-ghost" 
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                닫기
              </button>
            </div>
            {(() => {
              const questions = quiz?.questions || [];
              const answers = finalResults.answers || [];
              const wrongIdxs = finalResults.wrongIndexes || [];
              const total = questions.length;
              const correctCount = finalResults.correct;
              const pct = total > 0 ? Math.round((correctCount / total) * 100) : 0;
              const grade = (pct >= 80) ? 1 : (pct >= 50) ? 2 : 3;
              const label = (i) => ['A','B','C','D'][i] || String(i+1);
              
              return (
                <div style={{ marginTop: 12 }}>
                  {/* 요약 */}
                  <div style={{ 
                    border: '1px solid var(--gray-200)', 
                    borderRadius: 8, 
                    padding: 12, 
                    background: 'var(--gray-50)' 
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      gap: 16, 
                      flexWrap: 'wrap', 
                      alignItems: 'center' 
                    }}>
                      <div>맞은 문제: <strong>{correctCount}</strong> / {total}개</div>
                      <div>정답률: <strong>{pct}%</strong></div>
                      <div>등급: <span style={{ fontWeight: 800 }}>{grade}등급</span></div>
                      {wrongIdxs?.length > 0 && (
                        <div style={{ color: 'var(--gray-700)' }}>
                          틀린 문제: {wrongIdxs.join(', ')}번
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* 문항 상세 */}
                  <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
                    {questions.map((q, idx) => {
                      const selected = answers[idx];
                      const isWrong = wrongIdxs.includes(idx + 1) || (typeof selected === 'number' && selected !== q?.answerIndex);
                      
                      return (
                        <div 
                          key={idx} 
                          style={{ 
                            border: '1px solid var(--gray-200)', 
                            borderRadius: 10, 
                            padding: 14, 
                            background: isWrong ? 'rgba(239,68,68,0.06)' : 'rgba(16,185,129,0.05)' 
                          }}
                        >
                          <div style={{ fontWeight: 800, marginBottom: 6 }}>문제 {idx + 1}</div>
                          {q?.question && <div style={{ marginBottom: 10 }}>{q.question}</div>}
                          <div style={{ display: 'grid', gap: 6 }}>
                            {(q?.options || []).map((opt, i) => {
                              const isCorrect = i === q?.answerIndex;
                              const isSelected = i === selected;
                              const bg = isCorrect ? 'rgba(16,185,129,0.15)' : (isSelected ? 'rgba(239,68,68,0.12)' : 'transparent');
                              const border = isCorrect ? '1px solid #10b981' : (isSelected ? '1px solid #ef4444' : '1px solid var(--gray-200)');
                              
                              return (
                                <div 
                                  key={i} 
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 10, 
                                    padding: '8px 10px', 
                                    borderRadius: 8, 
                                    background: bg, 
                                    border 
                                  }}
                                >
                                  <div style={{ 
                                    width: 22, 
                                    height: 22, 
                                    borderRadius: 999, 
                                    background: '#0ea5e9', 
                                    color: '#fff', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    fontSize: 12, 
                                    fontWeight: 800 
                                  }}>
                                    {label(i)}
                                  </div>
                                  <div style={{ flex: 1 }}>{opt}</div>
                                  <div style={{ minWidth: 18, textAlign: 'right' }}>
                                    {isCorrect ? '✓' : (isSelected ? '✗' : '')}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </section>
  );
};

export default ShortFormQuizStudentPage;


